
import React, { useState } from 'react';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CreditCard, Download, Search, BarChart, Clock, CheckCircle, AlertCircle, Flame } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const GasBills = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const { toast } = useToast();

  // Mock data for gas bills
  const bills = [
    {
      id: 'GAS-001',
      month: 'April 2023',
      amount: 245.75,
      dueDate: '2023-05-15',
      status: 'Unpaid',
      consumption: 380,
      meterReading: '12450'
    },
    {
      id: 'GAS-002',
      month: 'March 2023',
      amount: 232.50,
      dueDate: '2023-04-15',
      status: 'Paid',
      consumption: 365,
      meterReading: '12070'
    },
    {
      id: 'GAS-003',
      month: 'February 2023',
      amount: 263.80,
      dueDate: '2023-03-15',
      status: 'Paid',
      consumption: 410,
      meterReading: '11705'
    },
    {
      id: 'GAS-004',
      month: 'January 2023',
      amount: 278.90,
      dueDate: '2023-02-15',
      status: 'Paid',
      consumption: 435,
      meterReading: '11295'
    }
  ];

  const filteredBills = bills.filter(bill => {
    return bill.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
           bill.month.toLowerCase().includes(searchTerm.toLowerCase()) ||
           bill.status.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handlePayNow = (bill: any) => {
    setSelectedBill(bill);
    setShowPaymentForm(true);
  };

  const handlePaymentComplete = () => {
    setShowPaymentForm(false);
    toast({
      title: "Payment Successful",
      description: `Your payment for ${selectedBill.month} has been processed successfully.`,
      variant: "default",
    });
  };

  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return <Badge className="bg-green-500">Paid</Badge>;
      case 'Unpaid':
        return <Badge className="bg-amber-500">Unpaid</Badge>;
      case 'Overdue':
        return <Badge className="bg-red-500">Overdue</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <main className="pt-24 pb-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <Flame className="h-6 w-6 text-amber-600 mr-2" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gas Utility Bills</h1>
          </div>
          <p className="mt-2 text-lg text-gray-700 dark:text-gray-300">
            Manage your gas bills and payments
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="px-6 py-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold">Gas Bills</CardTitle>
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search bills..."
                      className="pl-8 h-9 w-[200px] bg-background"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 py-0">
                <Tabs defaultValue="current" value={activeTab} onValueChange={setActiveTab}>
                  <div className="px-6">
                    <TabsList className="grid w-full grid-cols-3 mb-6">
                      <TabsTrigger value="current">Current</TabsTrigger>
                      <TabsTrigger value="history">History</TabsTrigger>
                      <TabsTrigger value="all">All Bills</TabsTrigger>
                    </TabsList>
                  </div>
                  
                  <TabsContent value="current" className="m-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="bg-muted/50">
                          <TableRow>
                            <TableHead className="font-medium">Bill ID</TableHead>
                            <TableHead className="font-medium">Month</TableHead>
                            <TableHead className="font-medium">Amount</TableHead>
                            <TableHead className="font-medium">Due Date</TableHead>
                            <TableHead className="font-medium">Status</TableHead>
                            <TableHead className="font-medium text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBills
                            .filter(bill => bill.status === 'Unpaid')
                            .map((bill) => (
                              <TableRow key={bill.id} className="hover:bg-muted/50">
                                <TableCell className="font-medium">{bill.id}</TableCell>
                                <TableCell>{bill.month}</TableCell>
                                <TableCell>${bill.amount.toFixed(2)}</TableCell>
                                <TableCell>{new Date(bill.dueDate).toLocaleDateString()}</TableCell>
                                <TableCell>{getBadgeColor(bill.status)}</TableCell>
                                <TableCell className="text-right">
                                  <Button onClick={() => handlePayNow(bill)} className="bg-amber-600 hover:bg-amber-700 text-white">
                                    <CreditCard className="mr-2 h-4 w-4" />
                                    Pay Now
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="history" className="m-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="bg-muted/50">
                          <TableRow>
                            <TableHead className="font-medium">Bill ID</TableHead>
                            <TableHead className="font-medium">Month</TableHead>
                            <TableHead className="font-medium">Amount</TableHead>
                            <TableHead className="font-medium">Consumption</TableHead>
                            <TableHead className="font-medium">Status</TableHead>
                            <TableHead className="font-medium text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBills
                            .filter(bill => bill.status === 'Paid')
                            .map((bill) => (
                              <TableRow key={bill.id} className="hover:bg-muted/50">
                                <TableCell className="font-medium">{bill.id}</TableCell>
                                <TableCell>{bill.month}</TableCell>
                                <TableCell>${bill.amount.toFixed(2)}</TableCell>
                                <TableCell>{bill.consumption} m³</TableCell>
                                <TableCell>{getBadgeColor(bill.status)}</TableCell>
                                <TableCell className="text-right">
                                  <Button variant="outline" size="sm">
                                    <Download className="mr-2 h-4 w-4" />
                                    Download
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="all" className="m-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="bg-muted/50">
                          <TableRow>
                            <TableHead className="font-medium">Bill ID</TableHead>
                            <TableHead className="font-medium">Month</TableHead>
                            <TableHead className="font-medium">Amount</TableHead>
                            <TableHead className="font-medium">Consumption</TableHead>
                            <TableHead className="font-medium">Status</TableHead>
                            <TableHead className="font-medium text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBills.map((bill) => (
                            <TableRow key={bill.id} className="hover:bg-muted/50">
                              <TableCell className="font-medium">{bill.id}</TableCell>
                              <TableCell>{bill.month}</TableCell>
                              <TableCell>${bill.amount.toFixed(2)}</TableCell>
                              <TableCell>{bill.consumption} m³</TableCell>
                              <TableCell>{getBadgeColor(bill.status)}</TableCell>
                              <TableCell className="text-right">
                                {bill.status === 'Unpaid' ? (
                                  <Button onClick={() => handlePayNow(bill)} className="bg-amber-600 hover:bg-amber-700 text-white">
                                    <CreditCard className="mr-2 h-4 w-4" />
                                    Pay Now
                                  </Button>
                                ) : (
                                  <Button variant="outline" size="sm">
                                    <Download className="mr-2 h-4 w-4" />
                                    Download
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
          
          <div>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Gas Consumption Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-60 flex items-center justify-center">
                  <div className="text-center">
                    <Flame className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-sm text-gray-500">Gas consumption chart will be displayed here</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Payment Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span>Paid</span>
                    </div>
                    <span className="font-medium">3</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-amber-500 mr-2" />
                      <span>Unpaid</span>
                    </div>
                    <span className="font-medium">1</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                      <span>Overdue</span>
                    </div>
                    <span className="font-medium">0</span>
                  </div>
                  
                  <div className="pt-4 mt-4 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-500">Next bill estimated:</span>
                      <span className="font-medium">$240.75</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Due date:</span>
                      <span className="font-medium">June 15, 2023</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GasBills;
